import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useDashboardDomainContext } from "../../DashboardDomainProvider";
import { Loading, IconControl } from 'spark-core-dx/components';
import IconSelectorDialog from '../dialogs/IconSelectorDialog';
import moment from 'moment-timezone';
import './DashboardHeader.scss';

// ⬇️ adjust this path if needed
import { useDatasetData } from '../../../../engine/dataset';

const DashboardHeader = ({onActionClicked}) => {
  const {
    dashboardUserNavStateLib,
    selectedTabData,
    dashboardLib,
    mode,
    isOwner
  } = useDashboardDomainContext();

  // ⬇️ grab potential requery helpers if your hook exposes them
  const {
    refreshDataset,
    onManualRefresh,     // preferred if available
    requeryLast,         // fallback
    requeryDataset,      // fallback
  } = useDatasetData();

  const isViewMode = mode === 'view';
  const [isIconDialogOpen, setIsIconDialogOpen] = useState(false);

  const {
    DraftDetails = {},
    PublishedDetails = {},
    UpdatedDate: draftDate,
    State: {
      PublishedDate: publishedDate,
      IsPublished: isPublished = false,
      IsPublic: isPublic = false,
      IsTemplate: isTemplate = false,
    } = {},
  } = selectedTabData ?? {};

  const {
    Name: draftName = "",
    Description: draftDescription = "",
    Icon: draftIconRaw,
  } = DraftDetails ?? {};

  const draftIcon = draftIconRaw || "edit";

  const {
    Name: publishedName = "",
    Description: publishedDescription = "",
    Icon: publishedIcon,
  } = PublishedDetails ?? {};

  isViewMode && !publishedName && isPublished && console.error("Published dashboards require a name");

  const [formName, setFormName] = useState(draftName);
  useEffect(() => { setFormName(draftName); }, [draftName]);

  const [formDescription, setFormDescription] = useState(draftDescription);
  useEffect(() => { setFormDescription(draftDescription); }, [draftDescription]);

  const formatDate = (date) => {
    if (!date) return "";
    return moment.tz(date, moment.tz.guess()).format('MM/DD/YY [at] h:mm a z');
  };

  const handleOpenIconPicker = () => setIsIconDialogOpen(true);
  const handleCloseIconPicker = () => setIsIconDialogOpen(false);

  // Resolve visuals for the currently active mode
  const activeVisuals = useMemo(() => {
    return (isViewMode ? PublishedDetails?.Visuals : DraftDetails?.Visuals) ?? [];
  }, [isViewMode, PublishedDetails?.Visuals, DraftDetails?.Visuals]);

  // Unique dataset IDs referenced by visuals on this dashboard
  const activeDatasetIds = useMemo(() => {
    const ids = new Set();
    for (const v of activeVisuals) {
      if (v?.DashboardDatasetId) ids.add(String(v.DashboardDatasetId));
    }
    return Array.from(ids);
  }, [activeVisuals]);

  // Silent “refresh all”
  const [refreshAll, setRefreshAll] = useState(false);
  const [refreshFailed, setRefreshfailed] = useState(false);
  const refreshInFlightRef = useRef(false);

  // Small helper: re-run last query if the hook exposes a way to do it
  const requeryIfPossible = useCallback(async (datasetId) => {
    try {
      if (typeof onManualRefresh === 'function') {
        await onManualRefresh(datasetId);
        return;
      }
      if (typeof requeryLast === 'function') {
        await requeryLast(datasetId);
        return;
      }
      if (typeof requeryDataset === 'function') {
        await requeryDataset(datasetId);
        return;
      }
      // no-op fallback: some engines auto-publish on refresh anyway
    } catch (e) {
      // don't explode UI; surface in tooltip via refreshFailed
      setRefreshfailed(e?.message || String(e));
      console.debug('[dashboard] requery failed', datasetId, e);
    }
  }, [onManualRefresh, requeryLast, requeryDataset]);

  
  const handleManageDatasets = useCallback(async () => {
    
      if(onActionClicked) {
        await onActionClicked("dataset");
      }
  });

  const handleManualRefreshAll = useCallback(async () => {
    if (refreshInFlightRef.current) return;
    if (activeDatasetIds.length === 0) return;

    refreshInFlightRef.current = true;
    setRefreshAll(true);
    setRefreshfailed(null);

    try {
      const ids = activeDatasetIds;
      const results = await Promise.allSettled(
        ids.map(async (id) => {
          await refreshDataset(id, { silent: true });
          await requeryIfPossible(id);
        })
      );
      // If any failed, capture one message for the tooltip
      const rejected = results.find(r => r.status === 'rejected');
      if (rejected) {
        setRefreshfailed(rejected.reason?.message || String(rejected.reason));
      }
    } finally {
      setRefreshAll(false);              // <- turn spinner off only after all are done
      refreshInFlightRef.current = false;
    }
  }, [activeDatasetIds, refreshDataset, requeryIfPossible]);

  const iconClass = [
    'dashboard-icon',
    `${isViewMode && !publishedIcon ? 'no-icon' : ''}`,
    `${!isViewMode && draftIcon === 'edit' ? 'no-selected-icon' : ''}`
  ].join(' ');

  return (
    (dashboardUserNavStateLib?.dashboardTabs?.length === 0 || (isViewMode && !isPublished))
      ? ''
      : (
        <div className={`dashboard-header ${isViewMode ? "view-mode" : "design-mode"}`}>
          {isTemplate && <div className="template-label">TEMPLATE</div>}

          <div className="header-top-section">
            <div className="header-left-section">
              <IconControl
                className={iconClass}
                iconKey={isViewMode ? (publishedIcon || "edit") : (draftIcon ?? "edit")}
                color={"var(--primary-text)"}
                onClick={isViewMode ? null : handleOpenIconPicker}
              />
              <input
                disabled={isViewMode}
                className="dashboard-title"
                value={isViewMode ? publishedName : formName}
                placeholder="Dashboard Name"
                onChange={e => setFormName(e.target.value)}
                onBlur={e => {
                  if (!isViewMode && e.target.value !== draftName) {
                    dashboardLib.handleDashNameChange(e.target.value);
                  }
                }}
              />
            </div>

            <div className="header-right-section">
              {onActionClicked && <IconControl
                    className={(refreshFailed ? ' error' : '')}
                    title = "Manage Datasets"
                    iconKey="database"
                    onClick={handleManageDatasets}
                  />}
              {activeDatasetIds.length > 0 && (
                !refreshAll ? (
                  <IconControl
                    className={(refreshFailed ? 'refresh-all error' : 'refresh-all')}
                    title={(refreshFailed ? `Failed to refresh dataset (Error: ${refreshFailed})` : 'Refresh all datasets.')}
                    iconKey="sync"
                    onClick={handleManualRefreshAll}
                  />
                ) : (
                  <Loading className="tiny-loading" type={'spinner-2'} />
                )
              )}

              {isOwner && isPublished && (!isViewMode || (isViewMode && isPublic)) && (
                <IconControl
                  className={`public-icon ${isPublic ? "" : "off-mode"}`}
                  title={isPublic ? "Public" : "Private"}
                  iconKey={isPublic ? "public" : "publicoff"}
                  onClick={!isViewMode ? () => dashboardLib.updatePublicState() : undefined}
                />
              )}
              <span className="dashboard-published-info">
                {isPublished ? "Published on " + formatDate(publishedDate) : "UNPUBLISHED"}
              </span>
            </div>
          </div>

          <div className="header-bottom-section">
            <div className="header-left-section">
              <input
                disabled={isViewMode}
                className={`dashboard-description ${isViewMode && !publishedDescription ? 'no-description' : ''}`}
                value={isViewMode ? publishedDescription : formDescription}
                placeholder="Dashboard Description"
                onChange={e => setFormDescription(e.target.value)}
                onBlur={e => {
                  if (!isViewMode && e.target.value !== draftDescription) {
                    dashboardLib.handleDashDescriptionChange(e.target.value);
                  }
                }}
              />
            </div>
            <div className="header-right-section">
              {!isViewMode && (
                <span className="dashboard-draft-info">
                  {"Draft Saved on " + formatDate(draftDate)}
                </span>
              )}
            </div>
          </div>

          <IconSelectorDialog
            isActive={isIconDialogOpen}
            closeDialog={handleCloseIconPicker}
          />
        </div>
      )
  );
};

export default DashboardHeader;
